import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Briefcase, Plus, Search, Eye, ArrowRight, Upload, FileText, X, Clock, AlertTriangle, CheckCircle2, Loader2, Link as LinkIcon, Mail, ArrowLeftRight, Trash2 } from "lucide-react";
import CountdownTimer from "../components/CountdownTimer";
import ResponsabilityWarningDialog from "../components/ResponsabilityWarningDialog";
import SendEmailDialog from "../components/SendEmailDialog";
import TransferRequestsCard from "../components/TransferRequestsCard";
import TransferRequestDialog from "../components/TransferRequestDialog";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  "Conakry",
  "Boké",
  "Kindia",
  "Mamou",
  "Labé",
  "Faranah",
  "Kankan",
  "Nzérékoré",
];

const SECTEURS = [
  "BTP, Génie Civil, Immobilier",
  "Agriculture, Agro-industrie, Elevage, Pêche",
  "Ambassade",
  "Automobile et Mécanique Générale",
  "Banques, IMF, Monnaies Electroniques, Assurances",
  "Diamantaires, Orpailleurs et Assimilés",
  "Enseignement Privé",
  "Environnement, Assainissement, Bois",
  "EPA",
  "Hydrocarbure et énergie",
  "Industrie Manufacturière, Commerce",
  "Institution",
  "Mines et Carrières",
  "Prestation de Services, Bureau d'Etude Technique",
  "SA Etatique",
  "Santé",
  "Sécurité Privée",
  "TIC, Télécom, Economie numérique, Presse",
  "Tourisme, Artisanat, Boulangerie, Patisserie",
  "Transport, Transit, Consignation",
];

export default function EnterprisesPage() {
  const { isAdmin, isCabinet } = useAuth();
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [regionFilter, setRegionFilter] = useState("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  // État pour l'envoi d'emails
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEnterprises, setSelectedEnterprises] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  
  const [formData, setFormData] = useState({
    raison_sociale: "",
    rccm: "",
    igt_id: "",
    secteur_activite: "",
    region: "",
    adresse: "",
    effectif_global: "",
    telephone: "",
    email: "",
    contrat_document: "",
  });
  const [contratFile, setContratFile] = useState(null);
  const [uploadingContrat, setUploadingContrat] = useState(false);
  // État pour les demandes de transfert
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [existingEnterpriseData, setExistingEnterpriseData] = useState(null);
  const [refreshTransfers, setRefreshTransfers] = useState(0);
  
  // État pour la suppression admin
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enterpriseToDelete, setEnterpriseToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchEnterprises();
  }, []);

  const fetchEnterprises = async () => {
    try {
      const response = await axios.get(`${API_URL}/enterprises`);
      setEnterprises(response.data);
    } catch (error) {
      console.error(/* PROD */ "Failed to fetch enterprises:", error);
      toast.error("Erreur lors du chargement des entreprises");
    } finally {
      setLoading(false);
    }
  };

  // Fonction de suppression d'entreprise (admin)
  const handleDeleteEnterprise = async () => {
    if (!enterpriseToDelete) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API_URL}/enterprises/${enterpriseToDelete.id}`);
      toast.success(response.data.message);
      if (response.data.bilans_deleted > 0) {
        toast.info(`${response.data.bilans_deleted} bilan(s) associé(s) également supprimé(s)`);
      }
      fetchEnterprises();
      setDeleteDialogOpen(false);
      setEnterpriseToDelete(null);
    } catch (error) {
      console.error(/* PROD */ "Delete error:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  // Reset form when dialog opens
  const handleDialogOpen = (open) => {
    setIsCreateOpen(open);
    if (open) {
      setFormData({
        raison_sociale: "",
        rccm: "",
        igt_id: "",
        secteur_activite: "",
        region: "",
        adresse: "",
        effectif_global: "",
        telephone: "",
        email: "",
        contrat_document: "",
      });
      setContratFile(null);
    }
  };

  const handleContratUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Type de fichier non autorisé. Utilisez PDF, JPG ou PNG.");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10MB)");
      return;
    }

    setUploadingContrat(true);
    try {
      const uploadData = new FormData();
      uploadData.append("file", file);

      const response = await axios.post(`${API_URL}/enterprises/upload-contrat`, uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setContratFile({
        name: file.name,
        filename: response.data.filename,
      });
      setFormData({ ...formData, contrat_document: response.data.filename });
      toast.success("Contrat téléchargé avec succès");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du téléchargement");
    } finally {
      setUploadingContrat(false);
    }
  };

  const removeContratFile = () => {
    setContratFile(null);
    setFormData({ ...formData, contrat_document: "" });
  };

  // Show warning dialog before creating
  const handleCreateClick = (e) => {
    e.preventDefault();
    setShowWarningDialog(true);
  };

  // Actually create the enterprise after confirmation
  const handleConfirmedCreate = async () => {
    setCreating(true);

    try {
      const dataToSend = {
        ...formData,
        effectif_global: parseInt(formData.effectif_global, 10),
      };
      await axios.post(`${API_URL}/enterprises`, dataToSend);
      toast.success("Entreprise créée avec succès");
      setShowWarningDialog(false);
      handleDialogOpen(false);
      fetchEnterprises();
    } catch (error) {
      // Gérer le cas d'entreprise existante dans un autre cabinet
      if (error.response?.status === 409) {
        const errorData = error.response.data?.detail;
        if (errorData?.code === "ENTERPRISE_EXISTS_OTHER_CABINET") {
          setShowWarningDialog(false);
          handleDialogOpen(false);
          // Préparer les données pour le dialogue de transfert
          setExistingEnterpriseData({
            enterprise: {
              id: errorData.enterprise_id,
              raison_sociale: errorData.enterprise_name,
              rccm: formData.rccm,
              nif: formData.nif,
              region: formData.region
            },
            current_cabinet: {
              id: errorData.current_cabinet_id,
              nom: errorData.current_cabinet_name
            }
          });
          setTransferDialogOpen(true);
          toast.warning(
            `Cette entreprise est déjà enregistrée chez "${errorData.current_cabinet_name}". Vous pouvez demander un transfert.`
          );
        } else {
          toast.error(error.response?.data?.detail || "Erreur lors de la création");
        }
      } else {
        toast.error(error.response?.data?.detail || "Erreur lors de la création");
      }
    } finally {
      setCreating(false);
    }
  };

  const filteredEnterprises = enterprises.filter((enterprise) => {
    const matchesSearch =
      enterprise.raison_sociale.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enterprise.rccm.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRegion = regionFilter === "all" || enterprise.region === regionFilter;
    return matchesSearch && matchesRegion;
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-40" />
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="enterprises-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Entreprises</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? "Toutes les entreprises déclarantes" : "Vos entreprises clientes"}
          </p>
        </div>
        {isCabinet && (
          <Dialog open={isCreateOpen} onOpenChange={handleDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="create-enterprise-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Entreprise
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Créer une Entreprise</DialogTitle>
                <DialogDescription>
                  Remplissez les informations de l'entreprise
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleCreateClick}>
                <div className="grid grid-cols-2 gap-4 py-4">
                  <div className="col-span-2">
                    <Label htmlFor="raison_sociale">Raison sociale *</Label>
                    <Input
                      id="raison_sociale"
                      value={formData.raison_sociale}
                      onChange={(e) => setFormData({ ...formData, raison_sociale: e.target.value })}
                      required
                      data-testid="enterprise-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="rccm">RCCM *</Label>
                    <Input
                      id="rccm"
                      value={formData.rccm}
                      onChange={(e) => setFormData({ ...formData, rccm: e.target.value })}
                      required
                      data-testid="enterprise-rccm-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="igt_id">ID IGT *</Label>
                    <Input
                      id="igt_id"
                      value={formData.igt_id}
                      onChange={(e) => setFormData({ ...formData, igt_id: e.target.value })}
                      required
                      placeholder="Ex: 10180126"
                      data-testid="enterprise-igt-id-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="secteur_activite">Secteur d'activité *</Label>
                    <Select
                      value={formData.secteur_activite}
                      onValueChange={(value) => setFormData({ ...formData, secteur_activite: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {SECTEURS.map((secteur) => (
                          <SelectItem key={secteur} value={secteur}>
                            {secteur}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="region">Région *</Label>
                    <Select
                      value={formData.region}
                      onValueChange={(value) => setFormData({ ...formData, region: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIONS.map((region) => (
                          <SelectItem key={region} value={region}>
                            {region}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="adresse">Adresse *</Label>
                    <Input
                      id="adresse"
                      value={formData.adresse}
                      onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="effectif_global">Effectif global *</Label>
                    <Input
                      id="effectif_global"
                      type="number"
                      min="1"
                      value={formData.effectif_global}
                      onChange={(e) => setFormData({ ...formData, effectif_global: e.target.value })}
                      required
                      data-testid="enterprise-effectif-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="telephone">Téléphone</Label>
                    <Input
                      id="telephone"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Copie du contrat Cabinet-Entreprise</Label>
                    {contratFile ? (
                      <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg mt-2">
                        <FileText className="w-5 h-5 text-emerald-600" />
                        <span className="flex-1 text-sm text-emerald-700 truncate">
                          {contratFile.name}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={removeContratFile}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <label
                          htmlFor="contrat-upload"
                          className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                            uploadingContrat
                              ? "bg-slate-100 border-slate-300"
                              : "border-slate-300 hover:border-primary hover:bg-primary/5"
                          }`}
                        >
                          {uploadingContrat ? (
                            <span className="text-sm text-slate-500">Téléchargement...</span>
                          ) : (
                            <>
                              <Upload className="w-5 h-5 text-slate-400" />
                              <span className="text-sm text-slate-600">
                                Cliquez pour télécharger le contrat (PDF, JPG, PNG - max 10MB)
                              </span>
                            </>
                          )}
                        </label>
                        <input
                          id="contrat-upload"
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleContratUpload}
                          className="hidden"
                          disabled={uploadingContrat}
                        />
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Document attestant la relation contractuelle entre votre cabinet et l'entreprise
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={creating} data-testid="submit-enterprise-btn">
                    {creating ? "Création..." : "Créer l'entreprise"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Compte à rebours - Date limite de déclaration */}
      {isCabinet && (
        <Card className="border-2 border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Date Limite de Déclaration
            </CardTitle>
            <CardDescription>
              Soumettez vos bilans sociaux avant le 31 mars pour éviter les pénalités
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CountdownTimer 
              deadline={`${new Date().getFullYear()}-03-31T23:59:59`}
              label={`Échéance: 31 mars ${new Date().getFullYear()}`}
            />
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-orange-700 font-semibold">
                  <AlertTriangle className="w-4 h-4" />
                  Retard (1-15 avril)
                </div>
                <p className="text-orange-600 mt-1">500 000 GNF / employé</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-red-700 font-semibold">
                  <AlertTriangle className="w-4 h-4" />
                  Défaut (après 15 avril)
                </div>
                <p className="text-red-600 mt-1">1 000 000 GNF / employé</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Demandes de transfert en attente */}
      <TransferRequestsCard 
        key={refreshTransfers}
        onTransferProcessed={() => {
          fetchEnterprises();
          setRefreshTransfers(prev => prev + 1);
        }} 
      />

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher par nom ou RCCM..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-enterprises"
              />
            </div>
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filtrer par région" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les régions</SelectItem>
                {REGIONS.map((region) => (
                  <SelectItem key={region} value={region}>
                    {region}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              variant={selectionMode ? "default" : "outline"}
              onClick={() => {
                setSelectionMode(!selectionMode);
                if (selectionMode) setSelectedEnterprises([]);
              }}
            >
              <Checkbox className="mr-2 h-4 w-4" checked={selectionMode} />
              Sélection
            </Button>
            {selectedEnterprises.length > 0 && (
              <Button 
                onClick={() => setEmailDialogOpen(true)}
                className="bg-primary"
              >
                <Mail className="w-4 h-4 mr-2" />
                Envoyer un email ({selectedEnterprises.length})
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredEnterprises.length === 0 ? (
            <div className="text-center py-12">
              <Briefcase className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-muted-foreground">Aucune entreprise trouvée</p>
              {isCabinet && (
                <Button className="mt-4" onClick={() => setIsCreateOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une entreprise
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {selectionMode && (
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectedEnterprises.length === filteredEnterprises.filter(e => e.email).length && filteredEnterprises.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedEnterprises(filteredEnterprises.filter(e => e.email).map(e => ({
                              id: e.id,
                              email: e.email,
                              name: e.raison_sociale,
                              type: "enterprise"
                            })));
                          } else {
                            setSelectedEnterprises([]);
                          }
                        }}
                      />
                    </TableHead>
                  )}
                  <TableHead>Entreprise</TableHead>
                  <TableHead>RCCM / NIF</TableHead>
                  <TableHead>Secteur</TableHead>
                  <TableHead>Région</TableHead>
                  <TableHead>Effectif</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEnterprises.map((enterprise) => (
                  <TableRow key={enterprise.id}>
                    {selectionMode && (
                      <TableCell>
                        <Checkbox 
                          checked={selectedEnterprises.some(e => e.id === enterprise.id)}
                          disabled={!enterprise.email}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedEnterprises([...selectedEnterprises, {
                                id: enterprise.id,
                                email: enterprise.email,
                                name: enterprise.raison_sociale,
                                type: "enterprise"
                              }]);
                            } else {
                              setSelectedEnterprises(selectedEnterprises.filter(e => e.id !== enterprise.id));
                            }
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <div>
                        <p className="font-medium">{enterprise.raison_sociale}</p>
                        {enterprise.email && (
                          <p className="text-sm text-muted-foreground">{enterprise.email}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-mono">{enterprise.rccm}</p>
                        <p className="font-mono text-muted-foreground">{enterprise.nif}</p>
                      </div>
                    </TableCell>
                    <TableCell>{enterprise.secteur_activite}</TableCell>
                    <TableCell>{enterprise.region}</TableCell>
                    <TableCell>
                      <span className="font-semibold">{enterprise.effectif_global}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/entreprises/${enterprise.id}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4 mr-2" />
                            Voir
                          </Button>
                        </Link>
                        {isAdmin && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setEnterpriseToDelete(enterprise);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Supprimer l'entreprise</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer cette entreprise ? Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          {enterpriseToDelete && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-semibold text-lg">{enterpriseToDelete.raison_sociale}</p>
                <p className="text-sm text-muted-foreground">RCCM: {enterpriseToDelete.rccm}</p>
                <p className="text-sm text-muted-foreground">Email: {enterpriseToDelete.email || "—"}</p>
                <p className="text-sm text-muted-foreground">Région: {enterpriseToDelete.region}</p>
              </div>
              <p className="text-sm text-red-600 mt-3">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Tous les bilans associés à cette entreprise seront également supprimés.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteEnterprise}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer définitivement
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warning Dialog for Enterprise Creation */}
      <ResponsabilityWarningDialog
        open={showWarningDialog}
        onOpenChange={setShowWarningDialog}
        onConfirm={handleConfirmedCreate}
        type="enterprise"
        loading={creating}
      />

      {/* Email Dialog */}
      <SendEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        recipients={selectedEnterprises}
        recipientType="enterprise"
        allowGroupSelection={isAdmin}
        title="Envoyer un email aux entreprises"
      />

      {/* Transfer Request Dialog - Quand une entreprise existe déjà */}
      <TransferRequestDialog
        open={transferDialogOpen}
        onOpenChange={setTransferDialogOpen}
        existingEnterprise={existingEnterpriseData}
        onTransferRequested={() => {
          setRefreshTransfers(prev => prev + 1);
          toast.success("Demande de transfert envoyée ! Vous serez notifié de la décision du cabinet propriétaire.");
        }}
      />
    </div>
  );
}
